import { useState } from 'react';
import { useApp } from '../../context/AppContext';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { PlusCircle, Trash2, TrendingUp, TrendingDown } from 'lucide-react';

const EntryList = ({ entries, onRemove, color, icon: Icon, label, currency, formatMoney }) => (
  <div className="space-y-2">
    {entries.map((e, i) => (
      <div key={i} className={`flex items-center gap-2 p-2 rounded-lg bg-${color}-50 dark:bg-${color}-900/20 border border-${color}-200 dark:border-${color}-800`}>
        <Icon className={`h-4 w-4 text-${color}-600 flex-shrink-0`} />
        <span className={`text-sm font-medium text-${color}-800 dark:text-${color}-200`}>
          {label} {e.year}: <strong>{formatMoney(e.amount)}</strong>
        </span>
        <Button variant="ghost" size="icon" className="h-7 w-7 ml-auto" onClick={() => onRemove(i)}>
          <Trash2 className="h-3.5 w-3.5 text-red-500" />
        </Button>
      </div>
    ))}
  </div>
);

export const TopUpWithdrawals = () => {
  const { inputs, updateInput, t, language, formatMoney, currency } = useApp();
  const [topUpYear, setTopUpYear] = useState(1);
  const [topUpAmount, setTopUpAmount] = useState(5000);
  const [withdrawalYear, setWithdrawalYear] = useState(10);
  const [withdrawalAmount, setWithdrawalAmount] = useState(10000);
  const [activeTab, setActiveTab] = useState('topup');

  const topUps = inputs.topUps || [];
  const withdrawals = inputs.withdrawals || [];

  const addTopUp = () => {
    if (topUpYear >= 1 && topUpYear <= inputs.years && topUpAmount > 0) {
      updateInput('topUps', [...topUps, { year: Number(topUpYear), amount: Number(topUpAmount) }].sort((a, b) => a.year - b.year));
    }
  };

  const removeTopUp = (idx) => {
    updateInput('topUps', topUps.filter((_, i) => i !== idx));
  };

  const addWithdrawal = () => {
    if (withdrawalYear >= 1 && withdrawalYear <= inputs.years && withdrawalAmount > 0) {
      updateInput('withdrawals', [...withdrawals, { year: Number(withdrawalYear), amount: Number(withdrawalAmount) }].sort((a, b) => a.year - b.year));
    }
  };

  const removeWithdrawal = (idx) => {
    updateInput('withdrawals', withdrawals.filter((_, i) => i !== idx));
  };

  const curr = currency === 'EUR' ? 'EUR' : 'RON';

  return (
    <div className="space-y-4">
      {/* Tab buttons */}
      <div className="flex gap-2">
        <button
          className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition-all ${activeTab === 'topup' ? 'bg-emerald-600 text-white' : 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300'}`}
          onClick={() => setActiveTab('topup')}
        >
          <TrendingUp className="h-4 w-4" />
          {t('topUps')}
          {topUps.length > 0 && <Badge className="ml-1 bg-white/30 text-current">{topUps.length}</Badge>}
        </button>
        <button
          className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition-all ${activeTab === 'withdraw' ? 'bg-amber-600 text-white' : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'}`}
          onClick={() => setActiveTab('withdraw')}
        >
          <TrendingDown className="h-4 w-4" />
          {t('withdrawals')}
          {withdrawals.length > 0 && <Badge className="ml-1 bg-white/30 text-current">{withdrawals.length}</Badge>}
        </button>
      </div>

      {activeTab === 'topup' && (
        <div className="p-4 rounded-2xl bg-emerald-50 dark:bg-emerald-900/20 border-2 border-emerald-200 dark:border-emerald-800 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs text-emerald-700 dark:text-emerald-300 font-semibold mb-1 block">{t('year')}</Label>
              <Input type="number" value={topUpYear} onChange={e => setTopUpYear(Number(e.target.value))}
                min={1} max={inputs.years} className="h-9" />
            </div>
            <div>
              <Label className="text-xs text-emerald-700 dark:text-emerald-300 font-semibold mb-1 block">{t('amount')} ({curr})</Label>
              <Input type="number" value={topUpAmount} onChange={e => setTopUpAmount(Number(e.target.value))}
                min={0} step={500} className="h-9" />
            </div>
          </div>
          <Button size="sm" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-full" onClick={addTopUp}>
            <PlusCircle className="h-4 w-4 mr-1.5" />{t('addTopUp')}
          </Button>
          {topUps.length > 0 && (
            <EntryList entries={topUps} onRemove={removeTopUp} color="emerald"
              icon={TrendingUp} label={language === 'ro' ? 'An' : 'Year'} currency={currency} formatMoney={formatMoney} />
          )}
        </div>
      )}

      {activeTab === 'withdraw' && (
        <div className="p-4 rounded-2xl bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-200 dark:border-amber-800 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs text-amber-700 dark:text-amber-300 font-semibold mb-1 block">{t('year')}</Label>
              <Input type="number" value={withdrawalYear} onChange={e => setWithdrawalYear(Number(e.target.value))}
                min={1} max={inputs.years} className="h-9" />
            </div>
            <div>
              <Label className="text-xs text-amber-700 dark:text-amber-300 font-semibold mb-1 block">{t('amount')} ({curr})</Label>
              <Input type="number" value={withdrawalAmount} onChange={e => setWithdrawalAmount(Number(e.target.value))}
                min={0} step={500} className="h-9" />
            </div>
          </div>
          <Button size="sm" className="w-full bg-amber-600 hover:bg-amber-700 text-white rounded-full" onClick={addWithdrawal}>
            <PlusCircle className="h-4 w-4 mr-1.5" />{t('addWithdrawal')}
          </Button>
          {withdrawals.length > 0 && (
            <EntryList entries={withdrawals} onRemove={removeWithdrawal} color="amber"
              icon={TrendingDown} label={language === 'ro' ? 'An' : 'Year'} currency={currency} formatMoney={formatMoney} />
          )}
        </div>
      )}
    </div>
  );
};

export default TopUpWithdrawals;
